import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { ProjectCard } from '@/components/ProjectCard';
import { Loader2, Building2, Calendar, Clock, Users, TrendingUp, LogOut } from 'lucide-react';

export const ClientDashboard = () => {
  const { user } = useAuth();

  // Fetch projects that the client has access to
  const { data: projects = [], isLoading, error } = useQuery({
    queryKey: ['client-projects', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      // Get projects the client has access to via client_project_access table
      const { data, error } = await supabase
        .from('client_project_access')
        .select(`
          project_id,
          projects (
            id,
            name,
            description,
            status,
            created_at,
            updated_at,
            image_url
          )
        `)
        .eq('is_active', true)
        .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`)
        .eq('team_member_id', user.id);

      if (error) {
        console.error('Error fetching client projects:', error);
        throw error;
      }

      // Extract projects from the response and remove duplicates
      const uniqueProjects = new Map();
      data?.forEach(item => {
        const project = Array.isArray(item.projects) ? item.projects[0] : item.projects;
        if (project && !uniqueProjects.has(project.id)) {
          uniqueProjects.set(project.id, project);
        }
      });
      
      return Array.from(uniqueProjects.values());
    },
    enabled: !!user?.id,
  });

  const getStatusStats = () => {
    const stats = {
      total: projects.length,
      planning: projects.filter(p => p.status === 'planning').length,
      live: projects.filter(p => p.status === 'live').length,
      closed: projects.filter(p => p.status === 'closed').length,
    };
    return stats;
  };

  const stats = getStatusStats();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted/20">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading your projects...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted/20">
        <div className="container mx-auto px-6 py-8">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-destructive/10 rounded-2xl p-8">
              <div className="w-16 h-16 bg-destructive/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="h-8 w-8 text-destructive" />
              </div>
              <h2 className="text-xl font-semibold text-destructive mb-2">Unable to Load Projects</h2>
              <p className="text-muted-foreground">
                We're experiencing technical difficulties. Please try refreshing the page or contact support if the issue persists.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20">
      {/* Header Section */}
      <div className="bg-white/80 backdrop-blur-sm border-b shadow-sm">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center">
              <div className="mr-2">
                <img src="/lovable-uploads/ee2c8437-420b-4547-809c-1dff637aa96f.png" alt="City & Sanctuary Logo" width="36" height="36" className="text-cs-purple dark:text-white" />
              </div>
              <div className="flex flex-col items-start">
                <span className="font-display text-xl font-bold my-px px-[12px] py-0 text-cs-purple">
                  CITY &amp; SANCTUARY
                </span>
              </div>
            </div>
            <div className="ml-auto flex items-center gap-4">
              <p className="text-muted-foreground">Welcome back! Here's an overview of your projects</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // Use the same logout logic as the header
                  window.location.href = '/login';
                  // Clear any stored auth data
                  localStorage.removeItem('session_token');
                }}
                className="flex items-center gap-2 text-cs-purple border-cs-purple hover:bg-cs-purple/10"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          {projects.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="border-0 bg-white/60 backdrop-blur-sm shadow-sm hover:shadow-md transition-all">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                      <p className="text-sm text-muted-foreground">Total Projects</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 bg-white/60 backdrop-blur-sm shadow-sm hover:shadow-md transition-all">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Clock className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{stats.planning}</p>
                      <p className="text-sm text-muted-foreground">Planning</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 bg-white/60 backdrop-blur-sm shadow-sm hover:shadow-md transition-all">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{stats.live}</p>
                      <p className="text-sm text-muted-foreground">Live</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 bg-white/60 backdrop-blur-sm shadow-sm hover:shadow-md transition-all">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{stats.closed}</p>
                      <p className="text-sm text-muted-foreground">Completed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        {projects.length === 0 ? (
          <div className="max-w-lg mx-auto text-center">
            <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-lg">
              <CardContent className="p-12">
                <div className="w-20 h-20 bg-muted/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="h-10 w-10 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-foreground">No Projects Yet</h3>
                <p className="text-muted-foreground leading-relaxed">
                  You don't have access to any projects at the moment. Your project manager will assign you to relevant projects, and they'll appear here.
                </p>
                <div className="mt-6 p-4 bg-muted/10 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Need access to a project? Contact your project manager for assistance.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-foreground mb-2">Your Projects</h2>
              <p className="text-muted-foreground">Click on any project to view details and track progress</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project) => (
                <div key={project.id} className="animate-fade-in">
                  <ProjectCard 
                    project={project}
                    onClick={() => window.location.href = `/project/${project.id}`}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};